import {
  BarChart3,
  KanbanSquare,
  LayoutPanelTop,
  ListChecks,
  Plus,
  Search,
  SlidersHorizontal,
  UserCheck,
} from "lucide-react";
import type {
  ItEstado,
  ItGroupBy,
  ItMeta,
  ItPrioridad,
  ItSortMode,
  ItView,
} from "../types/it";
import { getItStateLabel, itStateOrder } from "../types/it";

export type ItFilters = {
  search: string;
  searchType: "todo" | "codigo" | "titulo";
  estado: ItEstado | "";
  proyecto: string;
  responsable: string;
  prioridad: ItPrioridad | "";
  categoria: string;
  onlyMine: boolean;
};

type Props = {
  meta: ItMeta;
  categories: string[];
  filters: ItFilters;
  groupBy: ItGroupBy;
  sortMode: ItSortMode;
  activeView: ItView;
  totalIncidents: number;
  activeIncidents: number;
  reopenedCount: number;
  onFiltersChange: (patch: Partial<ItFilters>) => void;
  onGroupByChange: (value: ItGroupBy) => void;
  onSortModeChange: (value: ItSortMode) => void;
  onViewChange: (value: ItView) => void;
  onOpenCreate: () => void;
};

export function ItToolbar({
  meta,
  categories,
  filters,
  groupBy,
  sortMode,
  activeView,
  totalIncidents,
  activeIncidents,
  reopenedCount,
  onViewChange,
  onFiltersChange,
  onGroupByChange,
  onSortModeChange,
  onOpenCreate,
}: Props) {
  return (
    <section className="dashboard-component-shell overflow-hidden rounded-[28px] border border-[#dfe8e4] bg-white shadow-[0_18px_50px_rgba(15,23,42,.065)]">
      <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f8fbf9_55%,#eef5f1_100%)] px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#00d09c]/18 bg-[#00d09c]/9 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-[#0b4a2f]">
            <LayoutPanelTop size={14} />
            Filtros y vistas
          </div>

          <div className="inline-flex items-center gap-2 rounded-full border border-[#dfe8e4] bg-white px-3 py-1.5 text-xs font-bold text-slate-600">
            <SlidersHorizontal size={14} />
            {meta.users.length} usuarios
          </div>

          <button
            type="button"
            onClick={() => onFiltersChange({ onlyMine: !filters.onlyMine })}
            className={[
              "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-black transition",
              filters.onlyMine
                ? "border-[#0b4a2f] bg-[#0b4a2f] text-white"
                : "border-[#dfe8e4] bg-white text-slate-600 hover:border-[#00d09c]/24 hover:bg-[#00d09c]/8 hover:text-[#0b4a2f]",
            ].join(" ")}
          >
            <UserCheck size={14} />
            Mis incidencias
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <MiniMetric label="Total" value={String(totalIncidents)} />
          <MiniMetric label="Activas" value={String(activeIncidents)} />
          <MiniMetric label="Reabiertas" value={String(reopenedCount)} tone="amber" />

          {meta.permissions.can_create && (
            <button
              type="button"
              onClick={onOpenCreate}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[linear-gradient(135deg,#0b4a2f_0%,#0f5b3b_50%,#00a879_100%)] px-4 py-2 text-sm font-black text-white shadow-[0_14px_30px_rgba(11,74,47,.18)] transition hover:-translate-y-0.5"
            >
              <Plus size={15} />
              Nueva incidencia
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3 bg-[#fbfdfc] p-4">
        <div className="grid gap-2.5 xl:grid-cols-[minmax(0,1.25fr)_repeat(5,minmax(0,1fr))]">
          <div className="flex items-center gap-2 rounded-xl border border-[#dfe8e4] bg-white px-3.5 py-2.5 shadow-[0_7px_18px_rgba(15,23,42,.04)] focus-within:border-[#00d09c]/45 focus-within:ring-4 focus-within:ring-[#00d09c]/10">
            <Search size={16} className="text-slate-400" />
            <input
              value={filters.search}
              onChange={(event) =>
                onFiltersChange({ search: event.target.value })
              }
              className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400"
              placeholder="Buscar incidencia..."
            />
          </div>

          <select
            value={filters.searchType}
            onChange={(event) =>
              onFiltersChange({
                searchType: event.target.value as ItFilters["searchType"],
              })
            }
            className="field-control"
          >
            <option value="todo">Buscar en todo</option>
            <option value="codigo">Código</option>
            <option value="titulo">Título</option>
          </select>

          <select
            value={filters.estado}
            onChange={(event) =>
              onFiltersChange({
                estado: event.target.value as ItFilters["estado"],
              })
            }
            className="field-control"
          >
            <option value="">Todos los estados</option>
            {itStateOrder.map((estado) => (
              <option key={estado} value={estado}>
                {getItStateLabel(estado)}
              </option>
            ))}
          </select>

          <select
            value={filters.proyecto}
            onChange={(event) =>
              onFiltersChange({ proyecto: event.target.value })
            }
            className="field-control"
          >
            <option value="">Todos los proyectos</option>
            {meta.projects.map((project) => (
              <option key={project.id} value={String(project.id)}>
                {project.nombre}
              </option>
            ))}
            <option value="none">Sin proyecto</option>
          </select>

          <select
            value={filters.responsable}
            onChange={(event) =>
              onFiltersChange({ responsable: event.target.value })
            }
            className="field-control"
          >
            <option value="">Todos los responsables</option>
            {meta.users.map((user) => (
              <option key={user.id} value={String(user.id)}>
                {user.nombre}
              </option>
            ))}
          </select>

          <select
            value={filters.prioridad}
            onChange={(event) =>
              onFiltersChange({
                prioridad: event.target.value as ItFilters["prioridad"],
              })
            }
            className="field-control"
          >
            <option value="">Todas las prioridades</option>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>
        </div>

        <div className="grid gap-2.5 xl:grid-cols-[minmax(0,1fr)_auto_auto]">
          <select
            value={filters.categoria}
            onChange={(event) =>
              onFiltersChange({ categoria: event.target.value })
            }
            className="field-control"
          >
            <option value="">Todas las categorías</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <div className="inline-flex items-center rounded-full border border-[#dfe8e4] bg-white p-1">
            <SegmentButton
              active={groupBy === "estado"}
              onClick={() => onGroupByChange("estado")}
            >
              Por estado
            </SegmentButton>
            <SegmentButton
              active={groupBy === "proyecto"}
              onClick={() => onGroupByChange("proyecto")}
            >
              Por proyecto
            </SegmentButton>
          </div>

          <div className="inline-flex items-center rounded-full border border-[#dfe8e4] bg-white p-1">
            <SegmentButton
              active={sortMode === "manual"}
              onClick={() => onSortModeChange("manual")}
              dark
            >
              Manual
            </SegmentButton>
            <SegmentButton
              active={sortMode === "recentes"}
              onClick={() => onSortModeChange("recentes")}
              dark
            >
              Recientes
            </SegmentButton>
          </div>
        </div>

        <div className="grid gap-2.5 border-t border-[#edf3ef] pt-3 md:grid-cols-3">
          <ViewButton
            active={activeView === "tabla"}
            icon={ListChecks}
            title="Kanban"
            subtitle="Vista en filas"
            onClick={() => onViewChange("tabla")}
          />
          <ViewButton
            active={activeView === "kanban"}
            icon={KanbanSquare}
            title="Tabla"
            subtitle="Vista por columnas"
            onClick={() => onViewChange("kanban")}
          />
          <ViewButton
            active={activeView === "dashboard"}
            icon={BarChart3}
            title="Dashboard"
            subtitle="Métricas y carga"
            onClick={() => onViewChange("dashboard")}
          />
        </div>
      </div>
    </section>
  );
}

function ViewButton({
  active,
  icon: Icon,
  title,
  subtitle,
  onClick,
}: {
  active: boolean;
  icon: typeof KanbanSquare;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "group flex min-h-[62px] items-center gap-2.5 rounded-[18px] border px-3 py-2.5 text-left transition",
        active
          ? "border-[#0b4a2f] bg-[linear-gradient(135deg,#0b4a2f,#0f5b3b)] text-white shadow-[0_12px_28px_rgba(11,74,47,.17)]"
          : "border-[#dfe8e4] bg-white text-slate-600 hover:border-[#00d09c]/22 hover:bg-[#00d09c]/7 hover:text-[#0b4a2f]",
      ].join(" ")}
    >
      <span
        className={[
          "grid h-9 w-9 shrink-0 place-items-center rounded-xl border",
          active
            ? "border-white/10 bg-white/10 text-white"
            : "border-[#dfe8e4] bg-white text-slate-400 group-hover:text-[#0b4a2f]",
        ].join(" ")}
      >
        <Icon size={16} />
      </span>

      <span className="min-w-0">
        <span className="block text-sm font-black">{title}</span>
        <span className={active ? "text-[11px] font-semibold text-white/70" : "text-[11px] font-semibold text-slate-400"}>
          {subtitle}
        </span>
      </span>
    </button>
  );
}

function MiniMetric({
  label,
  value,
  tone = "green",
}: {
  label: string;
  value: string;
  tone?: "green" | "amber";
}) {
  return (
    <div className="rounded-full border border-[#dfe8e4] bg-white px-3 py-1.5 text-xs font-bold text-slate-700">
      <span className="mr-1.5 font-black uppercase tracking-[0.12em] text-slate-400">
        {label}
      </span>
      <span className={tone === "amber" ? "text-amber-700" : "text-slate-950"}>
        {value}
      </span>
    </div>
  );
}

function SegmentButton({
  active,
  onClick,
  children,
  dark = false,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
  dark?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full px-3 py-1.5 text-xs font-black transition",
        active
          ? dark
            ? "bg-slate-950 text-white"
            : "bg-[linear-gradient(135deg,#0b4a2f,#0f5b3b)] text-white"
          : "text-slate-500 hover:bg-[#00d09c]/8 hover:text-[#0b4a2f]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
